import React, { useState } from 'react';
import styled from 'styled-components';
import { FaBuilding } from 'react-icons/fa';
import Button from '../common/Button';
import LogoUpload from '../common/LogoUpload';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const InfoBox = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => `${theme.colors.primary}10`};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  margin-top: 1rem;
`;

const AdminTab: React.FC = () => {
  const [agency] = useState({ id: 'demo-agency', logo_url: null });
  
  const handleLogoUpdate = (logoUrl: string | null) => {
    console.log('Logo mis à jour:', logoUrl);
  };

  return (
    <Container>
      <SectionTitle>Paramètres de l'agence</SectionTitle>
      
      <Description>
        Configurez les paramètres généraux de votre agence, personnalisez l'apparence 
        et gérez les options avancées de l'application.
      </Description>
      
      <CardGrid>
        <Card>
          <CardHeader>
            <CardIcon>
              <FaBuilding />
            </CardIcon>
            <CardTitle>Informations de l'agence</CardTitle>
          </CardHeader>
          <CardDescription>
            Modifiez le nom, l'adresse et les coordonnées de votre agence. 
            Ces informations sont visibles par tous les membres.
          </CardDescription>
          <Button 
            type="button" 
            $variant="secondary" 
            disabled
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '0.5rem',
              width: '100%'
            }}
          >
            <FaBuilding /> Bientôt disponible
          </Button>
        </Card>
        
        <Card>
          <CardTitle>
            <FaBuilding />
            Logo de l'agence
          </CardTitle>
          <CardDescription>
            Personnalisez le logo qui apparaît dans l'en-tête de l'application.
          </CardDescription>
          <LogoUpload
            agencyId={agency.id}
            currentLogo={agency.logo_url}
            onLogoUpdate={handleLogoUpdate}
          />
        </Card>
      </CardGrid>
      
      <InfoBox>
        <strong>ℹ️ Note :</strong> Seuls les propriétaires d'agence ont accès à cette section. 
        Les modifications effectuées ici affectent tous les membres de votre agence.
      </InfoBox>
    </Container>
  );
};

export default AdminTab;
